import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface ReferralNotification {
  id: string;
  type: string;
  referee_name: string | null;
  days_awarded: number;
  is_read: boolean;
  created_at: string;
}

export const useReferralNotifications = () => {
  // Get user ID once and memoize to prevent hook ordering issues during HMR
  const auth = useAuth();
  const userId = useMemo(() => auth?.user?.id, [auth?.user?.id]);
  
  const [notifications, setNotifications] = useState<ReferralNotification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<ReferralNotification | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('referral_notifications')
        .select('*')
        .eq('user_id', userId)
        .eq('is_read', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setNotifications(data || []);
      
      // Show the first unread notification
      if (data && data.length > 0) {
        setCurrentNotification(data[0]);
      }
    } catch (error) {
      console.error('Error fetching referral notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await supabase
        .from('referral_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Show next notification or close
      setCurrentNotification(prev => {
        const remaining = notifications.filter(n => n.id !== notificationId);
        return remaining.length > 0 ? remaining[0] : null;
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [notifications]);

  const dismissCurrent = useCallback(() => {
    if (currentNotification) {
      markAsRead(currentNotification.id);
    }
  }, [currentNotification, markAsRead]);

  useEffect(() => {
    if (userId) {
      fetchNotifications();
    }
  }, [userId, fetchNotifications]);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('referral_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'referral_notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newNotification = payload.new as ReferralNotification;
          setNotifications(prev => [newNotification, ...prev]);
          setCurrentNotification(newNotification);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  return {
    notifications,
    currentNotification,
    loading,
    dismissCurrent,
    refetch: fetchNotifications,
  };
};
